import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { OrganisationStructurePreview } from '@/components/OrganisationStructurePreview';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import {
  type OrganisationStructure,
  organisationStructureService,
} from '../services/organisationStructureService';
import {
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { formatDateDdMmYyyy } from '../utils/dateUtils';
import { OrganisationStructureDrawer } from './OrganisationStructureDrawer';

type PanelMode = 'empty' | 'view' | 'edit' | 'add';

const orgStructureSetFilterParams = {
  buttons: ['reset', 'apply'],
} as const;

function formatOrgStructureDate(value: string | null | undefined) {
  if (!value) return '-';
  return formatDateDdMmYyyy(value.slice(0, 10));
}

function orgStructureFilterText(value: string | null | undefined) {
  return value?.trim() || '-';
}

export const OrganisationStructureManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const justSavedRef = useRef(false);
  const gridRef = useRef<AgGridReact>(null);

  const {
    data: rows = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['organisation-structure'],
    queryFn: () => organisationStructureService.getAll(),
    refetchOnMount: 'always',
  });

  const [panelMode, setPanelMode] = useState<PanelMode>('empty');
  const [selectedRow, setSelectedRow] = useState<OrganisationStructure | null>(null);
  const [editingRow, setEditingRow] = useState<OrganisationStructure | null>(null);

  const handleAdd = () => {
    setEditingRow(null);
    setPanelMode('add');
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this row?')) return;
    try {
      await organisationStructureService.delete(id);
      queryClient.invalidateQueries({ queryKey: ['organisation-structure'] });
      setSelectedRow(null);
      setPanelMode('empty');
    } catch (err) {
      toast.error(`Failed to delete row: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleRowClicked = useCallback((event: RowClickedEvent<OrganisationStructure>) => {
    if (!event.data) return;
    setSelectedRow(event.data);
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handlePreviewRowSelect = useCallback((row: OrganisationStructure) => {
    setSelectedRow(row);
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleAddClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      return;
    }
    setPanelMode('empty');
    setEditingRow(null);
  }, []);

  const handleEditClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingRow(null);
      return;
    }
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedRow(null);
  }, []);

  const columnDefs: ColDef<OrganisationStructure>[] = useMemo(
    () => [
      {
        field: 'department_name',
        headerName: 'Department',
        flex: 1,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: orgStructureSetFilterParams,
        filterValueGetter: (p) => orgStructureFilterText(p.data?.department_name),
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'team_name',
        headerName: 'Team',
        flex: 1,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: orgStructureSetFilterParams,
        filterValueGetter: (p) => orgStructureFilterText(p.data?.team_name),
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'employee_name',
        headerName: 'Employee',
        flex: 1,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: orgStructureSetFilterParams,
        filterValueGetter: (p) => orgStructureFilterText(p.data?.employee_name),
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'role',
        headerName: 'Role',
        flex: 1,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: orgStructureSetFilterParams,
        filterValueGetter: (p) => orgStructureFilterText(p.data?.role),
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 120,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: orgStructureSetFilterParams,
        filterValueGetter: (p) => orgStructureFilterText(p.data?.type),
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'start_date',
        headerName: 'Start',
        width: 110,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: orgStructureSetFilterParams,
        filterValueGetter: (p) => formatOrgStructureDate(p.data?.start_date),
        valueFormatter: (p) => formatOrgStructureDate(p.value),
      },
      {
        field: 'end_date',
        headerName: 'End',
        width: 110,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: orgStructureSetFilterParams,
        filterValueGetter: (p) => formatOrgStructureDate(p.data?.end_date),
        valueFormatter: (p) => formatOrgStructureDate(p.value),
      },
    ],
    []
  );

  const gridOptions = useMemo(
    () => ({
      ...agGridDefaultOptions,
      onRowClicked: handleRowClicked,
      rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    }),
    [handleRowClicked]
  );

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <div className="p-2">
      <style>{agGridThemeStyles}</style>
      <SettingsListDetailLayout
        detailOpen={detailOpen}
        detailVariant="default"
        detail={
          panelMode === 'add' ? (
            <OrganisationStructureDrawer
              isOpen={true}
              onClose={handleAddClose}
              onSave={() => {}}
              onEntrySaved={() => {
                justSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['organisation-structure'] });
                setPanelMode('empty');
                setSelectedRow(null);
                setEditingRow(null);
              }}
              row={null}
              isEdit={false}
            />
          ) : panelMode === 'edit' && editingRow ? (
            <OrganisationStructureDrawer
              isOpen={true}
              onClose={handleEditClose}
              onSave={(row) => setSelectedRow(row)}
              onEntrySaved={() => {
                justSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['organisation-structure'] });
                setPanelMode('view');
                setEditingRow(null);
              }}
              row={editingRow}
              isEdit
            />
          ) : panelMode === 'view' && selectedRow ? (
            <OrganisationStructureDrawer
              key={selectedRow.id}
              isOpen={true}
              readOnly
              row={selectedRow}
              onClose={handleViewClose}
              onSave={() => {}}
              onEditRequest={() => {
                setEditingRow(selectedRow);
                setPanelMode('edit');
              }}
              onDeleteRequest={() => void handleDelete(selectedRow.id)}
            />
          ) : null
        }
        list={
          <>
            <div className="mb-4 flex shrink-0 items-center justify-end">
              <Button type="button" onClick={handleAdd}>
                <PlusIcon data-icon="inline-start" />
                Add Row
              </Button>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-4 p-8 text-text-secondary">
                <Spinner className="size-10" />
                <p>Loading organisation structure...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center p-8 text-danger-600">
                <p>Error loading data: {error.message}</p>
                <Button type="button" className="mt-4" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            ) : rows.length === 0 ? (
              <p className="p-8 text-center text-text-secondary">
                No organisation structure rows found. Add your first row to get started.
              </p>
            ) : (
              <Tabs defaultValue="preview" className="flex min-h-0 flex-1 flex-col">
                <TabsList className="mb-4 shrink-0">
                  <TabsTrigger value="preview">Preview</TabsTrigger>
                  <TabsTrigger value="data">Data</TabsTrigger>
                </TabsList>
                <TabsContent value="preview" className="flex min-h-0 flex-1 flex-col">
                  <OrganisationStructurePreview rows={rows} onSelectRow={handlePreviewRowSelect} />
                </TabsContent>
                <TabsContent value="data" className="flex min-h-0 flex-1 flex-col">
                  <div
                    className="ag-theme-material min-h-0 w-full flex-1"
                    style={agGridSettingsContainerStyles}
                  >
                    <AgGridReact
                      ref={gridRef}
                      theme={agGridTheme}
                      rowData={rows}
                      columnDefs={columnDefs}
                      defaultColDef={agGridDefaultColDef}
                      gridOptions={gridOptions}
                      domLayout="normal"
                      getRowId={(params) => String(params.data.id)}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </>
        }
      />
    </div>
  );
};
